import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Trophy, Clock, Calendar, FileText, Monitor, BookOpen, Target } from 'lucide-react';
import { useFirebase } from '../contexts/FirebaseContext';

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { getUserDetails, getUserTestResults } = useFirebase();
  
  const [userInfo, setUserInfo] = useState(null);
  const [testResults, setTestResults] = useState({
    online: [],
    offline: []
  });
  const [activeTab, setActiveTab] = useState('online');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userId) {
      loadUserData();
    }
  }, [userId]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      
      // Load user basic information
      const user = await getUserDetails(userId);
      setUserInfo(user);
      
      // Load user test results
      const results = await getUserTestResults(userId);
      setTestResults(results);
      
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return null;
    try {
      let dateObj;
      
      // Handle Firestore timestamp
      if (date.toDate && typeof date.toDate === 'function') {
        dateObj = date.toDate();
      } 
      // Handle seconds timestamp
      else if (date.seconds) {
        dateObj = new Date(date.seconds * 1000);
      }
      // Handle regular date/string/number
      else {
        dateObj = new Date(date);
      }
      
      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        return null;
      }
      
      return dateObj.toLocaleDateString('en-GB');
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return null;
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getScoreColor = (score, total) => {
    const percentage = (score / total) * 100;
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const TestCard = ({ test, type }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center space-x-4">
          <div className={`p-3 rounded-lg ${type === 'online' ? 'bg-blue-100' : 'bg-purple-100'}`}>
            {type === 'online' ? (
              <Monitor className={`w-6 h-6 ${type === 'online' ? 'text-blue-600' : 'text-purple-600'}`} />
            ) : (
              <BookOpen className={`w-6 h-6 ${type === 'online' ? 'text-blue-600' : 'text-purple-600'}`} />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{test.testName || 'Untitled Test'}</h3>
            <p className="text-sm text-gray-500 mt-1">{test.homeContentName || test.category || 'General'}</p>
          </div>
        </div>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          test.status === 'completed' ? 'bg-green-100 text-green-800' : 
          test.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' : 
          'bg-gray-100 text-gray-800'
        }`}>
          {test.status || 'completed'}
        </span>
      </div>

      {type === 'offline' ? (
        // Offline test layout
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="text-center">
              <div className={`text-3xl font-bold ${getScoreColor(test.totalMarks || 0, 100)}`}>
                {test.totalMarks || 0}
              </div>
              <div className="text-sm text-gray-500 mt-1">Marks Scored</div>
            </div>
            <div className="text-center">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>
                  {formatDate(test.submitedDate) || 
                   formatDate(test.completedAt) || 
                   formatDate(test.createdAt) || 
                   'N/A'}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">Submitted Date</div>
            </div>
            {test.plan && (
              <div className="text-center">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Target className="w-4 h-4" />
                  <span className="font-medium">{test.plan}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">Plan</div>
              </div>
            )}
          </div>
          {test.homeContentName && (
            <div className="bg-gray-50 px-3 py-2 rounded-md">
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <FileText className="w-4 h-4" />
                <span className="font-medium">{test.homeContentName}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">Category</div>
            </div>
          )}
        </div>
      ) : (
        // Online test layout
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="text-center">
              <div className={`text-3xl font-bold ${getScoreColor(test.totalMarks || 0, 100)}`}>
                {test.totalMarks || 0}
              </div>
              <div className="text-sm text-gray-500 mt-1">Marks Scored</div>
            </div>
            <div className="text-center">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>
                  {formatDate(test.submitedDate) || 
                   formatDate(test.completedAt) || 
                   formatDate(test.createdAt) || 
                   'N/A'}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">Submitted Date</div>
            </div>
          </div>
          {(test.category || test.homeContentName) && (
            <div className="bg-gray-50 px-3 py-2 rounded-md">
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <FileText className="w-4 h-4" />
                <span className="font-medium">{test.category || test.homeContentName}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">Category</div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading user details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/users')}
                className="p-2 hover:bg-red-500 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <User className="w-8 h-8" />
                  <h1 className="text-2xl font-bold">User Details</h1>
                </div>
                <p className="text-red-100">
                  Test results for: <span className="font-medium">{userInfo?.name || userInfo?.email || 'Loading...'}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* User Info Card */}
        {userInfo && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-red-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900">{userInfo.name || 'N/A'}</h2>
                <p className="text-gray-600">{userInfo.email}</p>
                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                  <span>Joined: {formatDate(userInfo.createdAt)}</span>
                  <span>•</span>
                  <span>Total Tests: {(testResults.online?.length || 0) + (testResults.offline?.length || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('online')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'online'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Monitor className="w-4 h-4" />
                  <span>Online Tests ({testResults.online?.length || 0})</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('offline')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'offline'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-4 h-4" />
                  <span>Offline Tests ({testResults.offline?.length || 0})</span>
                </div>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'online' && (
              <div>
                {testResults.online && testResults.online.length > 0 ? (
                  <div className="grid gap-4">
                    {testResults.online.map((test, index) => (
                      <TestCard key={test.id || index} test={test} type="online" />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Monitor className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Online Tests</h3>
                    <p className="text-gray-600">This user hasn't taken any online tests yet.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'offline' && (
              <div>
                {testResults.offline && testResults.offline.length > 0 ? (
                  <div className="grid gap-4">
                    {testResults.offline.map((test, index) => (
                      <TestCard key={test.id || index} test={test} type="offline" />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Offline Tests</h3>
                    <p className="text-gray-600">This user hasn't taken any offline tests yet.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
